import { useHasAccess } from '@components/HasAccess'
import { User } from '@components/user/User'
import { BusinessProfile as BusinessProfileModel } from '@models/business/BusinessProfile'
import {
  getBusinessProfiles,
  selectBusinessProfiles,
  setBusinessProfileQuery,
} from '@store/business/BusinessProfileReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { extractSortDataFromSorterResult } from '@supplement-bacon/react-toolkit/utils'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import { TableColumnsType, TablePaginationConfig, Typography } from 'antd'
import { SorterResult } from 'antd/es/table/interface'
import { useEffect } from 'react'
import { BusinessProfileStatus } from '../businessProfileStatus/BusinessProfileStatus'

const { Text } = Typography

interface Props {
  companyId: number
}

const Table = ({ companyId }: Props) => {
  const dispatch = useAppDispatch()
  const { hasAccess } = useHasAccess()

  const { isOpen } = useSBRM()
  const businessProfiles = useAppSelector(selectBusinessProfiles())
  const { isLoading, query } = useAppSelector((state) => state.businessProfile)

  const businessProfilColumns: TableColumnsType<BusinessProfileModel> = [
    {
      title: 'Name',
      render: (record: BusinessProfileModel) => (
        <Text>
          {record.lastName} {record.firstName}
        </Text>
      ),
    },
    {
      title: 'Status',
      render: (record: BusinessProfileModel) => (
        <BusinessProfileStatus.StatusTag id={record.status} />
      ),
    },
    {
      title: 'App user',
      render: (record: BusinessProfileModel) =>
        record.appUser ? (
          <User.Avatar id={record.appUser} />
        ) : (
          <Text>{record.code}</Text>
        ),
    },
    {
      align: 'right',
      render: (record: BusinessProfileModel) => (
        <SBRM.Actions
          actions={
            record.canBeDeleted
              ? [SBRMAction.Update, SBRMAction.Delete, SBRMAction.View]
              : [SBRMAction.Update, SBRMAction.View]
          }
          entity={Entity.businessProfile}
          entityId={record.id}
          disabled={!hasAccess(Permission.manageBusiness)}
        />
      ),
    },
  ]

  const handleTableChange = (
    pagination: TablePaginationConfig,
    sorter:
      | SorterResult<BusinessProfileModel>
      | SorterResult<BusinessProfileModel>[]
  ) => {
    const newQuery = {
      ...query,
      pagination,
      filters: {
        company: companyId ? [Number(companyId)] : [],
      },

      ...extractSortDataFromSorterResult(sorter),
    }

    dispatch(setBusinessProfileQuery(newQuery))
    dispatch(getBusinessProfiles(newQuery))
  }

  useEffect(() => {
    if (isOpen) {
      return
    }
    const newQuery: TableParams = {
      ...query,
      filters: {
        company: companyId ? [companyId] : [],
      },
    }

    dispatch(setBusinessProfileQuery(newQuery))
    dispatch(getBusinessProfiles(newQuery))
  }, [isOpen])

  return (
    <>
      <PageTitle level={5} title={'Employees'} />
      <SBTable
        columns={businessProfilColumns}
        dataSource={businessProfiles}
        loading={isLoading}
        onChange={handleTableChange}
        pagination={query.pagination}
      />
    </>
  )
}

type TableType = { Table: typeof Table }

export { Table, type TableType }
